import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
    Button,
    CircularProgress,
    FormLabel,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    Select,
    TextField,
    InputAdornment
} from "@mui/material";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { collection, getDocs, doc, getDoc, updateDoc, increment, Timestamp } from 'firebase/firestore';
import { db } from '../../../Config/firebase.ts';
import { SubscriptionType } from '../../../enums.ts';

interface Subscription {
    id: string;
    Name: string;
    subscriptionType: string;
    cyclePriceArray: any[];
    Price?: number;
}

const TYPE_LABEL: Record<string, string> = {
    [SubscriptionType.Monthly]: 'Monthly',
    [SubscriptionType.MultiMonth]: 'Multi-Month',
    [SubscriptionType.Count]: 'Count',
    [SubscriptionType.Hours]: 'Hours',
};

function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

export default function MoveStudentModal() {
    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.MoveStudentModalOpen);
    const { LiveSubscriptionId, currentSubscriptionType, lastOrderStatus } = useAppSelector(
        (state: RootState) => state.ArbitrarySlice.ForMoveStudentModal
    );
    const ServiceProviderID = useAppSelector(selectEffectiveUID);
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);

    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>("");
    const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
    const [newPrice, setNewPrice] = useState<string>("");
    const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(0);
    const [studentName, setStudentName] = useState<string>("");
    const [currentBatchName, setCurrentBatchName] = useState<string>("");
    const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string>("");
    const [oldSubscriptionId, setOldSubscriptionId] = useState<string>("");

    const hasPendingInvoice = lastOrderStatus === "Pending";

    useEffect(() => {
        if (open && LiveSubscriptionId && ServiceProviderID) {
            fetchStudentDetails();
            fetchSubscriptions();
        }
    }, [open, LiveSubscriptionId, ServiceProviderID]);

    const fetchStudentDetails = async () => {
        try {
            const liveSubRef = doc(db, `ServiceProviders/${ServiceProviderID}/LiveSubscriptions`, LiveSubscriptionId);
            const liveSubDoc = await getDoc(liveSubRef);
            
            if (liveSubDoc.exists()) {
                const data = liveSubDoc.data();
                setStudentName(data.studentName || "");
                setCurrentBatchName(data.subscriptionName || "");
                
                let subId = "";
                if (data.subscriptionPlanRef && data.subscriptionPlanRef.id) {
                    subId = data.subscriptionPlanRef.id;
                } else if (data.subscriptionPlanRefId) {
                    subId = data.subscriptionPlanRefId;
                }
                
                setCurrentSubscriptionId(subId);
                setOldSubscriptionId(subId);
            }
        } catch (error) {
            console.error("Error fetching student details:", error);
        }
    };

    const fetchSubscriptions = async () => {
        try {
            const subscriptionsRef = collection(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`);
            const subscriptionsSnap = await getDocs(subscriptionsRef);

            const subs: Subscription[] = [];
            subscriptionsSnap.forEach((docSnap) => {
                const data = docSnap.data();
                // Allow ALL subscription types; only exclude the current batch
                if (docSnap.id !== currentSubscriptionId) {
                    subs.push({
                        id: docSnap.id,
                        Name: data.Name || data.name || "Unnamed Subscription",
                        subscriptionType: data.subscriptionType,
                        cyclePriceArray: data.cyclePriceArray || [],
                        Price: data.Price
                    });
                }
            });

            setSubscriptions(subs);
        } catch (error) {
            console.error("Error fetching subscriptions:", error);
            toast.error("Failed to load subscriptions");
        }
    };

    const handleSubscriptionChange = (subscriptionId: string) => {
        setSelectedSubscriptionId(subscriptionId);
        const sub = subscriptions.find(s => s.id === subscriptionId);
        setSelectedSubscription(sub || null);
        setSelectedPlanIndex(0);
        
        if (sub) {
            if (sub.subscriptionType === SubscriptionType.Monthly) {
                setNewPrice(sub.Price?.toString() || "");
            } else if (sub.cyclePriceArray && sub.cyclePriceArray.length > 0) {
                const firstPlan = sub.cyclePriceArray[0];
                setNewPrice(firstPlan.price?.toString() || firstPlan.Price?.toString() || "");
            }
        }
    };

    const handlePlanChange = (index: number) => {
        setSelectedPlanIndex(index);
        if (selectedSubscription && selectedSubscription.cyclePriceArray[index]) {
            const plan = selectedSubscription.cyclePriceArray[index];
            setNewPrice(plan.price?.toString() || plan.Price?.toString() || "");
        }
    };

    const handleClose = () => {
        resetState();
        Dispatch(closeModal("MoveStudentModalOpen"));
    };

    const resetState = () => {
        setSubscriptions([]);
        setSelectedSubscriptionId("");
        setSelectedSubscription(null);
        setNewPrice("");
        setSelectedPlanIndex(0);
        setStudentName("");
        setCurrentBatchName("");
        setCurrentSubscriptionId("");
        setOldSubscriptionId("");
    };

    const validateForm = () => {
        if (!selectedSubscriptionId) {
            toast.error("Please select a batch");
            return false;
        }

        if (!newPrice || parseFloat(newPrice) < 0) {
            toast.error("Please enter a valid price");
            return false;
        }

        // For MultiMonth / Count, a plan must be explicitly selected
        if (
            selectedSubscription &&
            (selectedSubscription.subscriptionType === SubscriptionType.MultiMonth ||
                selectedSubscription.subscriptionType === SubscriptionType.Count)
        ) {
            if (
                selectedSubscription.cyclePriceArray.length === 0 ||
                selectedPlanIndex < 0 ||
                selectedPlanIndex >= selectedSubscription.cyclePriceArray.length
            ) {
                toast.error("Please select a valid plan");
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        Dispatch(toggleLoading(true));

        try {
            if (!selectedSubscription) {
                toast.error("Please select a batch");
                return;
            }

            const liveSubRef = doc(db, `ServiceProviders/${ServiceProviderID}/LiveSubscriptions`, LiveSubscriptionId);
            const newSubRef = doc(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`, selectedSubscriptionId);

            const today = new Date();
            const todayTimestamp = Timestamp.fromDate(today);

            // Base fields common to all types
            const updateData: any = {
                Price: parseFloat(newPrice),
                subscriptionName: selectedSubscription.Name,
                subscriptionPlanRefId: selectedSubscriptionId,
                subscriptionPlanRef: newSubRef,
                subscriptionType: selectedSubscription.subscriptionType,
                cycle: selectedSubscription.subscriptionType,
                IsRecurringSubscription: true,
                status: "Active",
            };

            const destType = selectedSubscription.subscriptionType;

            if (destType === SubscriptionType.Monthly) {
                // Monthly: no billing window, clear plan-specific fields
                updateData.billingCycle = "";
                updateData.assignedPlanIndex = 0;
                updateData.assignedPlans = [];
                updateData.remainingCount = 0;
                updateData.pauseDaysLeft = 0;

            } else if (destType === SubscriptionType.MultiMonth) {
                const selectedPlan = selectedSubscription.cyclePriceArray[selectedPlanIndex];
                const updatedAssignedPlans = selectedSubscription.cyclePriceArray.map((plan: any, idx: number) =>
                    idx === selectedPlanIndex ? { ...plan, price: parseFloat(newPrice) } : plan
                );
                const validityDays = Number(selectedPlan.validity) || 0;
                const endDate = addDays(today, validityDays);

                updateData.billingCycle = selectedPlan.billingCycle;
                updateData.assignedPlanIndex = selectedPlanIndex;
                updateData.assignedPlans = updatedAssignedPlans;
                updateData.pauseDaysLeft = selectedPlan.pauseDays || 0;
                updateData.billingStart = todayTimestamp;
                updateData.billingEnd = Timestamp.fromDate(endDate);
                updateData.remainingCount = 0;

            } else if (destType === SubscriptionType.Count) {
                const updatedAssignedPlans = selectedSubscription.cyclePriceArray.map((plan: any, idx: number) =>
                    idx === selectedPlanIndex ? { ...plan, price: parseFloat(newPrice) } : plan
                );

                // Treat like a new student who hasn't paid yet:
                // zero count, no validity window — SP must raise an invoice to grant access.
                updateData.status = "InActive";
                updateData.billingCycle = "Count";
                updateData.assignedPlanIndex = selectedPlanIndex;
                updateData.assignedPlans = updatedAssignedPlans;
                updateData.remainingCount = 0;
                updateData.validity = 0;
                updateData.billingStart = todayTimestamp;
                updateData.billingEnd = todayTimestamp;
                updateData.pauseDaysLeft = 0;
            }

            await updateDoc(liveSubRef, updateData);
            
            // Update activeSubscribersCount on old and new subscription docs
            if (oldSubscriptionId && oldSubscriptionId !== selectedSubscriptionId) {
                const oldSubRef = doc(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`, oldSubscriptionId);
                await updateDoc(oldSubRef, {
                    activeSubscribersCount: increment(-1)
                });

                const newSubDocRef = doc(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`, selectedSubscriptionId);
                await updateDoc(newSubDocRef, {
                    activeSubscribersCount: increment(1)
                });
            }
            
            toast.success("Student moved successfully!");
            handleClose();
            window.location.reload();
            
        } catch (error: any) {
            console.error("Error moving student:", error);
            toast.error(error.message || "Failed to move student");
        } finally {
            Dispatch(toggleLoading(false));
        }
    };

    const isCrossType = selectedSubscription && selectedSubscription.subscriptionType !== currentSubscriptionType;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '12px',
                    padding: '8px'
                }
            }}
        >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Move Student</h2>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </div>

            <DialogContent sx={{ px: 3, py: 3 }}>
                {/* Student Info */}
                <div className="bg-blue-50 p-3 rounded-lg mb-5">
                    <p className="text-sm text-gray-600">Student Name</p>
                    <p className="text-base font-semibold text-gray-800">{studentName}</p>
                    <p className="text-xs text-gray-500 mt-1">Current Batch: {currentBatchName}</p>
                    <p className="text-xs text-gray-500">Current Type: <span className="font-medium">{TYPE_LABEL[currentSubscriptionType] || currentSubscriptionType}</span></p>
                </div>

                {/* Pending invoice block */}
                {hasPendingInvoice ? (
                    <>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5">
                            <p className="text-sm font-semibold text-red-700 mb-1">Cannot move student</p>
                            <p className="text-sm text-red-600">
                                This student has a <strong>pending invoice</strong>. Please cancel the pending invoice
                                in <strong>Orders</strong> before moving this student to another batch.
                            </p>
                        </div>
                        <div className="flex justify-end pt-2">
                            <Button
                                onClick={handleClose}
                                variant="outlined"
                                sx={{ textTransform: 'none', px: 3, py: 1 }}
                            >
                                Close
                            </Button>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-5 py-2">
                            {/* Select New Batch */}
                            <div>
                                <FormLabel sx={{ mb: 1, fontWeight: 400, fontSize: '16px' }}>
                                    Select New Batch *
                                </FormLabel>
                                <Select
                                    value={selectedSubscriptionId}
                                    onChange={(e) => handleSubscriptionChange(e.target.value)}
                                    displayEmpty
                                    required
                                    size="small"
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'grey',
                                            borderRadius: '10px'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'grey',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'skyblue',
                                        },
                                        '& .MuiSelect-select': {
                                            fontSize: '14px',
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled sx={{ fontSize: '14px' }}>
                                        Select a batch
                                    </MenuItem>
                                    {subscriptions.length === 0 ? (
                                        <MenuItem value="" disabled sx={{ fontSize: '14px' }}>
                                            No other batches available
                                        </MenuItem>
                                    ) : (
                                        subscriptions.map((sub) => (
                                            <MenuItem key={sub.id} value={sub.id} sx={{ fontSize: '14px' }}>
                                                <span>{sub.Name}</span>
                                                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded font-medium ${
                                                    sub.subscriptionType === SubscriptionType.Monthly
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : sub.subscriptionType === SubscriptionType.MultiMonth
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : sub.subscriptionType === SubscriptionType.Count
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {TYPE_LABEL[sub.subscriptionType] || sub.subscriptionType}
                                                </span>
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </div>

                            {/* Cross-type info banner */}
                            {isCrossType && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <p className="text-sm text-blue-800">
                                        <strong>Type change:</strong> Moving from{' '}
                                        <strong>{TYPE_LABEL[currentSubscriptionType] || currentSubscriptionType}</strong> to{' '}
                                        <strong>{TYPE_LABEL[selectedSubscription!.subscriptionType] || selectedSubscription!.subscriptionType}</strong>.
                                        The student will start a fresh cycle in the new batch and it will be marked as
                                        active (no invoice will be raised automatically).
                                    </p>
                                </div>
                            )}

                            {/* Plan Selection for MultiMonth and Count */}
                            {selectedSubscription &&
                                (selectedSubscription.subscriptionType === SubscriptionType.MultiMonth ||
                                selectedSubscription.subscriptionType === SubscriptionType.Count) &&
                                selectedSubscription.cyclePriceArray &&
                                selectedSubscription.cyclePriceArray.length > 0 && (
                                <div>
                                    <FormLabel sx={{ mb: 1, fontWeight: 400, fontSize: '16px' }}>
                                        Select Plan *
                                    </FormLabel>
                                    <RadioGroup
                                        value={selectedPlanIndex}
                                        onChange={(e) => handlePlanChange(Number(e.target.value))}
                                    >
                                        {selectedSubscription.cyclePriceArray.map((plan, index) => (
                                            <FormControlLabel
                                                key={index}
                                                value={index}
                                                control={<Radio />}
                                                label={
                                                    selectedSubscription.subscriptionType === SubscriptionType.Count
                                                        ? `${plan.count || plan.Count} classes · ${plan.validity || plan.Validity} days · ₹${plan.price || plan.Price}`
                                                        : `${plan.billingCycle} (${plan.validity} days) · ₹${plan.price}`
                                                }
                                                sx={{
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    padding: '8px 12px',
                                                    marginBottom: '8px',
                                                    '&:hover': {
                                                        backgroundColor: '#f9fafb',
                                                    },
                                                }}
                                            />
                                        ))}
                                    </RadioGroup>
                                </div>
                            )}

                            {/* New Price */}
                            <div>
                                <FormLabel sx={{ mb: 1, fontWeight: 400, fontSize: '16px' }}>
                                    New Price *
                                </FormLabel>
                                <TextField
                                    type="number"
                                    value={newPrice}
                                    onChange={(e) => setNewPrice(e.target.value)}
                                    placeholder="Enter price"
                                    required
                                    size="small"
                                    inputProps={{ min: 0, step: "0.01" }}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                            },
                                        },
                                        fontWeight: 400,
                                        fontSize: "1px",
                                    }}
                                />
                            </div>

                            {/* Warning Message */}
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                <p className="text-sm text-yellow-800">
                                    <strong>Note:</strong> Moving the student will update their batch assignment
                                    and pricing details. This action cannot be undone.
                                </p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-200">
                            <Button
                                onClick={handleClose}
                                variant="outlined"
                                disabled={isLoading}
                                sx={{
                                    textTransform: 'none',
                                    px: 3,
                                    py: 1,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isLoading}
                                sx={{
                                    textTransform: 'none',
                                    px: 3,
                                    py: 1,
                                    backgroundColor: '#7c3aed',
                                    '&:hover': {
                                        backgroundColor: '#6d28d9',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#d1d5db',
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <CircularProgress size={20} style={{ marginRight: '8px', color: 'white' }} />
                                        Moving...
                                    </>
                                ) : (
                                    'Move Student'
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
